import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import CircularLoader from "../Ui/Loader";
import ButtonComponent from "../Ui/Button";
import useApiHandler from "../hooks/ApiHandler";
import { WarrantyRequests } from "../../service";
import { humanizeEnum } from "../utils/utils";

const statusStyles = {
  SUBMITTED: "bg-[#FFF7E8] text-[#B86E00] border border-[#FFD699]",
  APPROVED: "bg-[#F2FBF4] text-[#2F8F48] border border-[#B9E7C7]",
  DENIED: "bg-[#FFF1F1] text-[#C62828] border border-[#F4BBBB]",
  RESOLVED: "bg-[#F3F6FB] text-[#49627A] border border-[#D9E3EF]",
};

const formatIssue = (value) => {
  if (!value || typeof value !== "string") return "N/A";
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
};

const formatEnumValue = (value) => {
  if (!value || typeof value !== "string") return "N/A";
  const trimmed = value.trim();
  if (!trimmed) return "N/A";
  const looksLikeEnum = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/.test(trimmed);
  return looksLikeEnum ? humanizeEnum(trimmed) : trimmed;
};

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[12px] uppercase tracking-[0.08em] text-[#7A7A7A]">{label}</span>
    <span className="text-[14px] text-[#242424] break-words">{value || "N/A"}</span>
  </div>
);

const MediaList = ({ title, items }) => {
  if (!items?.length) return null;

  return (
    <div className="border border-[#ECECEC] rounded-[16px] p-4 bg-white">
      <h3 className="text-[16px] font-medium text-[#242424]">{title}</h3>
      <div className="mt-3 flex flex-col gap-3">
        {items.map((item) => (
          <a
            key={item.id}
            href={item.fileUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-[12px] border border-[#ECECEC] px-4 py-3 text-[14px] text-[#242424] hover:border-[#3CAB5C]"
          >
            {item.originalName || "Open attachment"}
          </a>
        ))}
      </div>
    </div>
  );
};

const WarrantyClaimReviewPage = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const { callApi, loading } = useApiHandler();
  const [claim, setClaim] = React.useState(null);
  const [reviewNotes, setReviewNotes] = React.useState("");
  const [decisionLoading, setDecisionLoading] = React.useState(false);

  const loadClaim = React.useCallback(async () => {
    if (!claimId) return;
    await callApi(() => WarrantyRequests.getById(claimId), {
      onSuccess: (response) => {
        const data = response?.data || response;
        setClaim(data);
        setReviewNotes(data?.reviewNotes || "");
      },
    });
  }, [callApi, claimId]);

  React.useEffect(() => {
    loadClaim();
  }, [loadClaim]);

  const handleApprove = async () => {
    if (!claim?.id) return;
    setDecisionLoading(true);
    await callApi(() => WarrantyRequests.approve(claim.id, { review_notes: reviewNotes }), {
      onSuccess: (response) => {
        const data = response?.data || response;
        setClaim(data);
        toast.success("Warranty claim approved. Schedule the repair from Past Jobs.");
      },
      finallyCallback: () => setDecisionLoading(false),
    });
  };

  const handleDeny = async () => {
    if (!claim?.id) return;
    if (!reviewNotes.trim()) {
      toast.error("Add a reason before denying this claim.");
      return;
    }
    setDecisionLoading(true);
    await callApi(() => WarrantyRequests.deny(claim.id, { review_notes: reviewNotes.trim() }), {
      onSuccess: (response) => {
        const data = response?.data || response;
        setClaim(data);
        toast.success("Warranty claim denied.");
      },
      finallyCallback: () => setDecisionLoading(false),
    });
  };

  const maintenanceRequest = claim?.maintenanceRequestDetails || {};
  const propertyAddress = maintenanceRequest?.propertyAddress || {};
  const statusKey = String(claim?.status || "").toUpperCase();
  const isSubmitted = statusKey === "SUBMITTED";
  const isInProgress = String(maintenanceRequest?.status || "").toUpperCase() === "IN_PROGRESS";

  return (
    <div className="pt-[103px] max-w-[1440px] m-auto md:px-6 px-4 pb-12">
      <Toaster />
      {loading && !claim ? (
        <CircularLoader minHeight="400px" />
      ) : claim ? (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-[12px] uppercase tracking-[0.08em] text-[#7A7A7A]">Warranty Claim</p>
              <h1 className="text-[28px] md:text-[36px] font-medium text-[#242424]">
                Job #{claim?.jobNumber || maintenanceRequest?.jobNumber || claimId}
              </h1>
              <p className="text-[15px] text-[#595959] mt-2 max-w-[760px]">
                Review the claim details, compare them to the original job, then approve it for warranty scheduling or deny the claim.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full text-[13px] font-medium ${statusStyles[statusKey] || "bg-[#F6F6F6] text-[#595959] border border-[#ECECEC]"}`}>
                {claim?.statusDisplay || formatEnumValue(statusKey) || "Unknown"}
              </span>
              <ButtonComponent onClick={() => navigate(isInProgress ? "/active-jobs" : "/past-jobs")}>
                {isInProgress ? "Go to Active Jobs" : "Back to Past Jobs"}
              </ButtonComponent>
            </div>
          </div>

          <div className="grid xl:grid-cols-[1.1fr_0.9fr] gap-6">
            <div className="flex flex-col gap-6">
              <div className="border border-[#ECECEC] rounded-[20px] bg-white p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-[20px] font-medium text-[#242424]">Claim Details</h2>
                    <p className="text-[14px] text-[#595959] mt-1">
                      Submitted by {claim?.submittedBy?.fullName || claim?.submittedBy?.email || "Unknown"} on {formatDateTime(claim?.createdAt)}.
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5 mt-6">
                  <DetailRow label="Warranty #" value={claim?.warrantyNumber || claim?.warranty_number || claim?.id} />
                  <DetailRow label="Service Category" value={formatIssue(claim?.serviceCategory || claim?.service_category)} />
                  <DetailRow label="Property" value={claim?.property} />
                  <DetailRow label="Current Status" value={claim?.statusDisplay || formatEnumValue(claim?.status)} />
                </div>

                <div className="mt-5">
                  <span className="text-[12px] uppercase tracking-[0.08em] text-[#7A7A7A]">Reported Issue</span>
                  <div className="mt-2 rounded-[16px] border border-[#ECECEC] bg-[#FAFAFA] p-4 text-[14px] text-[#242424] whitespace-pre-wrap">
                    {claim?.details || "No additional details were provided."}
                  </div>
                </div>

                <div className="mt-5">
                  <span className="text-[12px] uppercase tracking-[0.08em] text-[#7A7A7A]">Vendor Notes</span>
                  <textarea
                    value={reviewNotes}
                    onChange={(event) => setReviewNotes(event.target.value)}
                    placeholder="Add review notes. A reason is required if you deny the claim."
                    className="mt-2 min-h-[140px] w-full rounded-[16px] border border-[#ECECEC] px-4 py-3 text-[14px] text-[#242424] outline-none focus:border-[#3CAB5C]"
                    disabled={!isSubmitted || decisionLoading}
                  />
                </div>

                {!isSubmitted && (
                  <div className="mt-5 rounded-[16px] border border-[#ECECEC] bg-[#FAFAFA] p-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <DetailRow label="Reviewed By" value={claim?.reviewedBy?.fullName || claim?.reviewedBy?.email} />
                      <DetailRow label="Reviewed At" value={formatDateTime(claim?.reviewedAt)} />
                      <DetailRow label="Resolved At" value={formatDateTime(claim?.resolvedAt)} />
                    </div>
                  </div>
                )}

                {statusKey === "APPROVED" && !isInProgress && (
                  <div className="mt-5 rounded-[16px] border border-[#D7E8FF] bg-[#F5F9FF] p-4 text-[14px] text-[#1F4F82]">
                    The claim is approved. Next, schedule the warranty repair from the Past Jobs page.
                  </div>
                )}

                {isSubmitted && (
                  <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
                    <ButtonComponent onClick={handleDeny} bgColor="#FFFFFF" color="#C62828" borderColor="#F0B8B8" disabled={decisionLoading}>
                      {decisionLoading ? "Saving..." : "Deny Claim"}
                    </ButtonComponent>
                    <ButtonComponent onClick={handleApprove} bgColor="#3CAB5C" color="#FFFFFF" disabled={decisionLoading}>
                      {decisionLoading ? "Saving..." : "Approve Claim"}
                    </ButtonComponent>
                  </div>
                )}
              </div>

              <MediaList title="Claim Attachments" items={claim?.media || []} />
            </div>

            <div className="flex flex-col gap-6">
              <div className="border border-[#ECECEC] rounded-[20px] bg-white p-6">
                <h2 className="text-[20px] font-medium text-[#242424]">Original Job Details</h2>
                <p className="text-[14px] text-[#595959] mt-1">
                  This is the original maintenance request tied to the warranty claim.
                </p>

                <div className="grid md:grid-cols-2 gap-5 mt-6">
                  <DetailRow label="Job #" value={maintenanceRequest?.jobNumber} />
                  <DetailRow
                    label="Job Title"
                    value={maintenanceRequest?.subIssueTypeDisplay || formatIssue(maintenanceRequest?.subIssueType || maintenanceRequest?.issueType)}
                  />
                  <DetailRow label="Original Status" value={maintenanceRequest?.statusDisplay || formatEnumValue(maintenanceRequest?.status)} />
                  <DetailRow label="Completed At" value={formatDateTime(maintenanceRequest?.completedAt)} />
                  <DetailRow
                    label="Assigned Vendor"
                    value={maintenanceRequest?.assignedVendor?.fullName || maintenanceRequest?.assignedVendor?.email}
                  />
                  <DetailRow label="Address" value={[
                    propertyAddress?.streetAddress,
                    propertyAddress?.unitApt ? `Apt ${propertyAddress.unitApt}` : null,
                    [propertyAddress?.city, propertyAddress?.state, propertyAddress?.zipCode].filter(Boolean).join(" "),
                  ].filter(Boolean).join(", ")} />
                </div>

                <div className="mt-5">
                  <span className="text-[12px] uppercase tracking-[0.08em] text-[#7A7A7A]">Original Description</span>
                  <div className="mt-2 rounded-[16px] border border-[#ECECEC] bg-[#FAFAFA] p-4 text-[14px] text-[#242424] whitespace-pre-wrap">
                    {maintenanceRequest?.description || "No original description was provided."}
                  </div>
                </div>
              </div>

              <MediaList title="Original Job Attachments" items={maintenanceRequest?.media || []} />
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-[20px] border border-[#ECECEC] bg-white p-8 text-center">
          <h1 className="text-[24px] font-medium text-[#242424]">Warranty claim not available</h1>
          <p className="text-[14px] text-[#595959] mt-2">
            This claim could not be loaded or you do not have access to it.
          </p>
          <div className="mt-6 flex justify-center">
            <ButtonComponent onClick={() => navigate("/past-jobs")}>Back to Past Jobs</ButtonComponent>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarrantyClaimReviewPage;
